import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import { updateMemberStatus, removeTeamMember, logActivity, resendInvite, deleteTeamMember } from '../../../services/teamService';
import { usePermissions } from '../../../hooks/usePermissions';

const TeamMemberTable = ({ members, onMemberClick, onRefresh }) => {
  const [actionMenuOpen, setActionMenuOpen] = useState(null);
  const [loadingId, setLoadingId] = useState(null);
  const [resendCooldowns, setResendCooldowns] = useState({});
  const [toast, setToast] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null); // member to confirm delete

  const { role } = usePermissions();
  const isAdmin = role === 'admin';

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  };

  const getRoleBadgeColor = (role) => {
    const colors = {
      admin: 'bg-error/10 text-error',
      operator: 'bg-accent/10 text-accent',
      viewer: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300'
    };
    return colors?.[role] || colors?.viewer;
  };

  const getStatusBadgeColor = (status) => {
    const colors = {
      active: 'bg-success/10 text-success',
      inactive: 'bg-muted text-muted-foreground',
      pending: 'bg-warning/10 text-warning'
    };
    return colors?.[status] || colors?.pending;
  };

  const formatRole = (role) => role?.charAt(0)?.toUpperCase() + role?.slice(1);
  const formatStatus = (status) => status?.charAt(0)?.toUpperCase() + status?.slice(1);

  const handleStatusChange = async (e, member, newStatus) => {
    e?.stopPropagation();
    setActionMenuOpen(null);
    setLoadingId(member?.id);
    try {
      await updateMemberStatus(member?.id, newStatus);
      await logActivity('update', 'team_member', member?.id, { field: 'status', value: newStatus });
      onRefresh?.();
    } catch (err) {
      console.error('Status update error:', err);
    } finally {
      setLoadingId(null);
    }
  };

  const handleDeactivate = async (e, member) => {
    e?.stopPropagation();
    setActionMenuOpen(null);
    if (!window.confirm(`Deactivate ${member?.fullName}? They will lose access to the system.`)) return;
    setLoadingId(member?.id);
    try {
      await removeTeamMember(member?.id);
      onRefresh?.();
    } catch (err) {
      console.error('Deactivate error:', err);
    } finally {
      setLoadingId(null);
    }
  };

  const handleDelete = async () => {
    if (!deleteConfirm) return;
    const member = deleteConfirm;
    setDeleteConfirm(null);
    setActionMenuOpen(null);
    setLoadingId(member?.id);
    try {
      await deleteTeamMember(member?.id, member?.isPendingInvite);
      await logActivity('delete', 'team_member', member?.id, { name: member?.fullName });
      showToast(`${member?.fullName} has been deleted`);
      onRefresh?.();
    } catch (err) {
      console.error('Delete error:', err);
      showToast(err?.message || 'Failed to delete team member', 'error');
    } finally {
      setLoadingId(null);
    }
  };

  const handleResendInvite = async (e, member) => {
    e?.stopPropagation();
    setActionMenuOpen(null);
    setLoadingId(member?.id);
    try {
      const result = await resendInvite(member);
      if (result?.alreadyExists) {
        showToast(result?.message || 'This email already has an account. They can log in directly.', 'info');
      } else {
        showToast(`Invitation resent to ${member?.email}`);
        // Start 60-second cooldown
        setResendCooldowns(prev => ({ ...prev, [member?.id]: Date.now() + 60000 }));
        setTimeout(() => {
          setResendCooldowns(prev => {
            const updated = { ...prev };
            delete updated?.[member?.id];
            return updated;
          });
        }, 60000);
      }
    } catch (err) {
      console.error('Resend invite error:', err);
      showToast(err?.message || 'Failed to resend invitation', 'error');
    } finally {
      setLoadingId(null);
    }
  };

  const isResendCoolingDown = (memberId) => {
    return resendCooldowns?.[memberId] && resendCooldowns?.[memberId] > Date.now();
  };

  const getResendCooldownSeconds = (memberId) => {
    if (!resendCooldowns?.[memberId]) return 0;
    return Math.ceil((resendCooldowns?.[memberId] - Date.now()) / 1000);
  };

  const toggleMenu = (e, memberId) => {
    e?.stopPropagation();
    setActionMenuOpen(prev => prev === memberId ? null : memberId);
  };

  if (!members?.length) {
    return (
      <div className="bg-card rounded-xl p-8 md:p-12 text-center">
        <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-muted flex items-center justify-center">
          <Icon name="Users" size={32} className="text-muted-foreground" />
        </div>
        <h3 className="text-lg font-semibold text-foreground mb-2">No team members found</h3>
        <p className="text-sm text-muted-foreground">Try adjusting your filters or add new team members.</p>
      </div>
    );
  }

  return (
    <>
      {/* Delete Confirmation Dialog */}
      {deleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-card rounded-xl shadow-xl w-full max-w-md p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-error/10 flex items-center justify-center flex-shrink-0">
                <Icon name="Trash2" size={20} className="text-error" />
              </div>
              <div>
                <h3 className="text-base font-semibold text-foreground">Delete Team Member</h3>
                <p className="text-sm text-muted-foreground">This action cannot be undone</p>
              </div>
            </div>
            <p className="text-sm text-foreground mb-2">
              Are you sure you want to permanently delete <span className="font-semibold">{deleteConfirm?.fullName}</span>?
            </p>
            <p className="text-sm text-muted-foreground mb-6">
              This will remove them from all project assignments and cannot be reversed.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="px-4 py-2 text-sm font-medium rounded-lg border border-border text-foreground hover:bg-muted transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                className="px-4 py-2 text-sm font-medium rounded-lg bg-error text-white hover:bg-error/90 transition-colors"
              >
                Delete Permanently
              </button>
            </div>
          </div>
        </div>
      )}

      {toast && (
        <div className={`fixed top-4 right-4 z-[1100] flex items-center gap-2 px-4 py-3 rounded-lg shadow-lg text-sm font-medium transition-all ${
          toast?.type === 'error' ? 'bg-error text-white' : toast?.type === 'info' ? 'bg-primary text-white' : 'bg-success text-white'
        }`}>
          <Icon name={toast?.type === 'error' ? 'AlertCircle' : toast?.type === 'info' ? 'Info' : 'CheckCircle'} size={16} />
          {toast?.message}
        </div>
      )}
      <div className="bg-card rounded-xl shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-muted/50 border-b border-border">
              <tr>
                <th className="px-4 md:px-6 py-3 text-left">
                  <span className="text-sm font-semibold text-foreground">Member</span>
                </th>
                <th className="px-4 md:px-6 py-3 text-left hidden md:table-cell">
                  <span className="text-sm font-semibold text-foreground">Department</span>
                </th>
                <th className="px-4 md:px-6 py-3 text-left">
                  <span className="text-sm font-semibold text-foreground">Role</span>
                </th>
                <th className="px-4 md:px-6 py-3 text-left hidden lg:table-cell">
                  <span className="text-sm font-semibold text-foreground">Status</span>
                </th>
                <th className="px-4 md:px-6 py-3 text-left hidden lg:table-cell">
                  <span className="text-sm font-semibold text-foreground">Phone</span>
                </th>
                <th className="px-4 md:px-6 py-3 text-left">
                  <span className="text-sm font-semibold text-foreground">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {members?.map((member) => (
                <tr
                  key={member?.id}
                  className="border-b border-border last:border-0 hover:bg-muted/30 transition-colors cursor-pointer"
                  onClick={() => onMemberClick(member)}
                >
                  <td className="px-4 md:px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0 bg-muted">
                        <Image
                          src={member?.avatarUrl || `https://ui-avatars.com/api/?name=${encodeURIComponent(member?.fullName || 'User')}&background=1B3A6B&color=fff`}
                          alt={`Profile picture of ${member?.fullName}`}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-foreground">{member?.fullName}</p>
                        <p className="text-xs text-muted-foreground">{member?.email}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 md:px-6 py-4 hidden md:table-cell">
                    <p className="text-sm text-foreground">{member?.department || 'N/A'}</p>
                    <p className="text-xs text-muted-foreground">{member?.jobTitle || ''}</p>
                  </td>
                  <td className="px-4 md:px-6 py-4">
                    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium ${getRoleBadgeColor(member?.role)}`}>
                      {formatRole(member?.role)}
                    </span>
                  </td>
                  <td className="px-4 md:px-6 py-4 hidden lg:table-cell">
                    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium ${getStatusBadgeColor(member?.status)}`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${
                        member?.status === 'active' ? 'bg-success' :
                        member?.status === 'inactive' ? 'bg-muted-foreground' : 'bg-warning'
                      }`} />
                      {formatStatus(member?.status)}
                    </span>
                  </td>
                  <td className="px-4 md:px-6 py-4 hidden lg:table-cell">
                    <p className="text-sm text-foreground">{member?.phone || 'N/A'}</p>
                  </td>
                  <td className="px-4 md:px-6 py-4">
                    <div className="flex items-center gap-2">
                      {member?.isPendingInvite && (
                        <button
                          onClick={(e) => {
                            e?.stopPropagation();
                            if (!isResendCoolingDown(member?.id) && loadingId !== member?.id) {
                              handleResendInvite(e, member);
                            }
                          }}
                          disabled={loadingId === member?.id || isResendCoolingDown(member?.id)}
                          title={isResendCoolingDown(member?.id) ? `Wait ${getResendCooldownSeconds(member?.id)}s before resending` : 'Resend invite email'}
                          className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium bg-warning/10 text-warning hover:bg-warning/20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {loadingId === member?.id ? (
                            <div className="w-3 h-3 border-2 border-warning border-t-transparent rounded-full animate-spin" />
                          ) : (
                            <Icon name="Send" size={12} />
                          )}
                          {isResendCoolingDown(member?.id)
                            ? `${getResendCooldownSeconds(member?.id)}s`
                            : 'Resend Invite'}
                        </button>
                      )}
                      <div className="relative">
                        <button
                          onClick={(e) => toggleMenu(e, member?.id)}
                          disabled={loadingId === member?.id}
                          className="p-2 hover:bg-muted rounded-lg transition-colors disabled:opacity-50"
                        >
                          {loadingId === member?.id && !member?.isPendingInvite ? (
                            <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                          ) : (
                            <Icon name="MoreVertical" size={18} className="text-muted-foreground" />
                          )}
                        </button>

                        {actionMenuOpen === member?.id && (
                          <>
                            <div
                              className="fixed inset-0 z-10"
                              onClick={(e) => { e?.stopPropagation(); setActionMenuOpen(null); }}
                            />
                            <div className="absolute right-0 top-full mt-1 w-48 bg-card border border-border rounded-lg shadow-lg z-20 py-1">
                              <button
                                onClick={(e) => { e?.stopPropagation(); onMemberClick(member); setActionMenuOpen(null); }}
                                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-foreground hover:bg-muted transition-colors"
                              >
                                <Icon name="Edit" size={14} />
                                Edit Details
                              </button>

                              <div className="border-t border-border my-1" />
                              <p className="px-3 py-1 text-xs font-medium text-muted-foreground">Set Status</p>

                              {member?.status !== 'active' && (
                                <button
                                  onClick={(e) => handleStatusChange(e, member, 'active')}
                                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-success hover:bg-muted transition-colors"
                                >
                                  <Icon name="CheckCircle" size={14} />
                                  Set Active
                                </button>
                              )}
                              {member?.status !== 'pending' && (
                                <button
                                  onClick={(e) => handleStatusChange(e, member, 'pending')}
                                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-warning hover:bg-muted transition-colors"
                                >
                                  <Icon name="Clock" size={14} />
                                  Set Pending
                                </button>
                              )}
                              {member?.status !== 'inactive' && (
                                <button
                                  onClick={(e) => handleDeactivate(e, member)}
                                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-error hover:bg-muted transition-colors"
                                >
                                  <Icon name="UserX" size={14} />
                                  Deactivate
                                </button>
                              )}

                              {isAdmin && (
                                <>
                                  <div className="border-t border-border my-1" />
                                  <button
                                    onClick={(e) => { e?.stopPropagation(); setDeleteConfirm(member); setActionMenuOpen(null); }}
                                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-error hover:bg-error/10 transition-colors"
                                  >
                                    <Icon name="Trash2" size={14} />
                                    Delete Permanently
                                  </button>
                                </>
                              )}
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
};

export default TeamMemberTable;